// ======================= React & Style ======================
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import 'draft-js/dist/Draft.css';
// ======================= Component ==========================
import Mention from '../Mention';
import EditorMotion from './EditorMotion';
import { getScrollBarStyles } from '../../config/styles';
// ======================= Utils ==============================
import {
  ContentBlock,
  Editor,
  EditorState,
  getDefaultKeyBinding,
  Modifier,
  RichUtils,
} from 'draft-js';
import {
  appendDecoratedMessage,
  getCaretPosition,
  getCurrentBlock,
} from './utils';

// ====================== TypeScript ========================
interface IStyledProps {
  PropsHeight: string;
  PropsMinHeight?: string;
  editorFocused?: boolean;
}

interface IProps {
  confirmMutation: (postMutation: any, preUpload?: any) => void;
  setEditorState: (inputValue: any) => void;
  editorState: any;
  postMutation: () => void;
  preUpload?: any;
  height: string;
  minHeight?: string;
  userList: any;
  isMainChannel: boolean;
  setMentionedUsers?: any;
  setRedirectUrl?: any;
  mentionedUsers?: any;
  mentionId?: any;
  setMentionId?: any;
  selectorPos: number;
  setSelectorPos: any;
  activeTag?: string;
  disablePlaceholder?: boolean;
}

const EditorContainer = styled.div`
  max-height: calc(${(props: IStyledProps) => props.PropsHeight} + 32px);
  padding: 12px;
  border: 1px solid #e5e5e5;
  position: relative;
  background-color: #ffffff;
  &:before {
    position: absolute;
    ${(props: IStyledProps) =>
      props.editorFocused ? `width: 100%` : `width: 0`};
    top: 0;
    left: 0;
    background-color: #ffffff;
    z-index: 2;
    content: '';
    height: 1px;
    transition: width 400ms ease-out;
  }
`;

const InputContainer = styled.div`
  padding: 0;
  height: 100%;
  ${(props: IStyledProps) =>
    props.PropsMinHeight ? `min-height: ${props.PropsMinHeight}` : ''};
  max-height: ${(props: IStyledProps) => props.PropsHeight};
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  position: relative;
  font-weight: 500;
  color: #333;
  line-height: 1.57;
  ${getScrollBarStyles(2)}
  > .DraftEditor-root {
    .public-DraftEditor-content {
      ${(props: IStyledProps) =>
        props.PropsMinHeight ? `min-height: ${props.PropsMinHeight}` : ''};
    }
  }
`;

export default function TextInputArea(props: IProps) {
  const [isMention, toggleMention] = useState(false);
  const [currentMotionKey, setCurrentMotionKey] = useState('');
  const [userSelectedIndex, setUserSelectedIndex] = useState(0);
  const [windowPos, setWindowPos] = useState({
    position: 'fixed',
    top: 0,
    left: 0,
  });
  const [candidates, setCandidate] = useState(props.userList);
  const [editorFeatures, setEditorFeatures] = useState(false);
  const [editorStylesActive, setEditorStylesActive] = useState(false);
  const [editorFocused, setEditorFocused] = useState(false);
  const [preventMouseHover, setPreventMouseHover] = useState(false);
  const dropdownRef: any = useRef(null);
  const textAreaRef: any = useRef(null);
  // ==========================  REACT HOOKS =======================
  function handleInputClick(e: any) {
    if (
      (e.target.closest('.DraftEditor-editorContainer') &&
        !e.target.closest('.ChatContainer') &&
        props.activeTag === '') ||
      e.target.closest('.feature-button') ||
      e.target.closest('.Dropdown-users')
    ) {
      setEditorFocused(true);
    } else {
      setEditorFeatures(false);
      setEditorFocused(false);
    }
    if (isMention && !e.target.closest('.Dropdown-users')) {
      toggleMention(false);
    }
  }

  const setPointerEvent = (type: string) => {
    if (!dropdownRef && !dropdownRef.current) {
      return;
    }
    const dropDown: any =
      dropdownRef.current.ref.current.getElementsByClassName('item');
    for (const children of dropDown) {
      children.style.pointerEvents = type;
    }
  };

  const handleMouseMove = () => {
    setPreventMouseHover(false);
  };

  // Enable/Disable hover efect for users
  useEffect(() => {
    if (preventMouseHover && dropdownRef.current) {
      setPointerEvent('none');
    } else {
      if (!dropdownRef.current) {
        return;
      }
      setPointerEvent('auto');
    }
  }, [preventMouseHover]);

  // On mouse move e.g user use mouse, enable Hover efect
  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  useEffect(() => {
    // If mention list was close, reset selected index. And enable hover efect
    if (!isMention) {
      setUserSelectedIndex(0);
      setPreventMouseHover(false);
    } else {
      setPreventMouseHover(true);
    }
  }, [isMention]);

  useEffect(() => {
    document.addEventListener('mousedown', handleInputClick, false);

    // Specify how to clean up after this effect:
    return () => {
      document.removeEventListener('mousedown', handleInputClick, true);
    };
  });

  // using props.replyTo add mention information in the beginning of text editor
  // if replyTo is not null when mount the editor component,
  // find the certain name, if match, add to editor.
  useEffect(() => {
    if (props.mentionId) {
      const replyUser = props.userList.find((user: any) => {
        if (user.id === props.mentionId) {
          return true;
        }
        return false;
      });

      if (replyUser) {
        confirmMentionMessage(replyUser);
        props.setMentionId('');
      }
    }
    // eslint-disable-next-line
  }, [props.mentionId]);

  useEffect(() => {
    // Using the direct reference to the dropdown component once it is mounted override the global event listeners
    // blocking the space click in the input
    if (dropdownRef.current) {
      dropdownRef.current.selectItemOnEnter = () => {
        return;
      };
    }
  });

  // =================== Editor Event Handler ===========================

  /*
    Paste handler handles all url paste which have format: 'http******' for
    both https or http url.
   */
  function handlerPasteText(pastedText: string) {
    // trim pasted message to avoid space at front
    const trimmedText = pastedText.trim();
    // if it's a 'http**********'
    if (trimmedText.indexOf('http') === 0 && trimmedText.indexOf(' ') === -1) {
      // Set decorator to IMMUTABLE LINK
      const newEditorState = appendDecoratedMessage(
        'LINK',
        'IMMUTABLE',
        { url: trimmedText },
        props.editorState,
        trimmedText,
        props.selectorPos,
      );
      // set new caret position after append url to editor
      const caretPosition = getCaretPosition(newEditorState);
      props.setSelectorPos(caretPosition);
      props.setEditorState(newEditorState);

      return 'handled';
    }
    return 'not-handled';
  }

  /*
    calculate mention table position, make it fixed to the top based on the line
    number(line number calculation is broken by other commit )
  */
  const getMentionPosition = () => {
    const keyName = props.editorState.getSelection().getStartKey();
    const lineNumber = props.editorState
      .getCurrentContent()
      .getBlockMap()
      .keySeq()
      .findIndex((k: any) => k === keyName);

    const selection = document.getSelection();
    if (selection) {
      const range = selection.getRangeAt(0).cloneRange();
      const rect = range.getBoundingClientRect();

      if (rect.bottom === 0 && rect.left === 0) {
        const canvasElement = document.getElementById('CanvasImage');
        if (canvasElement !== null) {
          const canvasRect = canvasElement.getBoundingClientRect();
          return {
            position: 'fixed',
            top:
              canvasRect.bottom + 71 + (lineNumber < 4 ? lineNumber : 3) * 18,
            left: canvasRect.left + 44,
          };
        }

        return {
          position: 'absolute',
          top: 12 + lineNumber * 20,
          left: 24,
        };
      }
      return { position: 'fixed', top: rect.bottom - 8, left: rect.left + 16 };
    } else {
      return { position: 'fixed', top: 0, left: 0 };
    }
  };

  // append mention message to the editor.
  function confirmMentionMessage(selectedCandidate: any) {
    // generate mentioned message
    const mentionText = '@' + selectedCandidate.name;
    const currUsers = props.mentionedUsers;
    // manage list for delete/modify check
    currUsers.push(selectedCandidate.id);
    props.setMentionedUsers(currUsers);

    // set mutable mention decorator
    let newEditorState = appendDecoratedMessage(
      'MENTION',
      'MUTABLE',
      selectedCandidate,
      props.editorState,
      mentionText,
      props.selectorPos,
    );
    // After mention message, add space.
    const selection = newEditorState.getSelection();
    const contentState = newEditorState.getCurrentContent();
    const newContentState = Modifier.insertText(contentState, selection, ' ');
    newEditorState = EditorState.push(
      newEditorState,
      newContentState,
      'insert-characters',
    );
    // set new caret position after append mention message
    const caretPosition = getCaretPosition(newEditorState);
    props.setSelectorPos(caretPosition);
    props.setEditorState(newEditorState);

    toggleMention(false);
  }

  // style for selected text
  function handleSelection(editorState: EditorState) {
    const block = getCurrentBlock(editorState);
    const selectionState = editorState.getSelection();
    const start = selectionState.getStartOffset();
    const end = selectionState.getEndOffset();
    const text = getText(editorState, start, end, block);

    if (selectionState.getHasFocus()) {
      if (text.length > 0) {
        setEditorFeatures(true);
      } else {
        setEditorFeatures(false);
      }
    }
  }

  const getText = (
    editorState: EditorState,
    start: number,
    end: number,
    contentBlock?: ContentBlock,
  ) => {
    const block = contentBlock || getCurrentBlock(editorState);
    if (block) {
      const blockText = block.getText();
      return blockText.substring(start, end);
    } else {
      return '';
    }
  };

  const mentionChangeHandler = (editorState: EditorState) => {
    // if not main channel disable mention
    if (!props.isMainChannel) {
      return 'not-handled';
    }
    const selection = editorState.getSelection();
    const content = editorState.getCurrentContent();
    const currentBlockKey = selection.getAnchorKey();
    const blockText = content.getBlockForKey(currentBlockKey).getText();
    const anchorIndex = selection.getAnchorOffset() - 1;
    const latestInputChar = blockText.charAt(anchorIndex);
    let longestNameLength = 0;
    props.userList.forEach((user: any) => {
      longestNameLength =
        user.name.length > longestNameLength
          ? user.name.length
          : longestNameLength;
    });

    // if text is not change, disable window for caret move.
    if (
      props.editorState.getCurrentContent().getPlainText() ===
      editorState.getCurrentContent().getPlainText()
    ) {
      toggleMention(false);
      return 'not-handled';
    }

    // when type @ before, and anchor back to this position, do not open the mention list
    if (latestInputChar === '@') {
      props.setSelectorPos(anchorIndex);
      setCandidate(props.userList);
      toggleMention(true);
      setWindowPos(getMentionPosition());
      setEditorStylesActive(false);
      return 'not-handled';
    }

    // find nearest @ with from current anchor to before
    const textBeforeAnchor = blockText.substr(0, anchorIndex + 1);
    const atSymbolIndex = textBeforeAnchor.lastIndexOf('@');
    if (atSymbolIndex === -1) {
      toggleMention(false);
      return 'not-handled';
    } else {
      // find matched substring with user names.
      // if matches, update selectorPos and currentCandidates
      const lowerTextBetweenAtSymbolAndAnchor = textBeforeAnchor
        .substr(atSymbolIndex + 1)
        .toLowerCase();

      const currCandidates = props.userList.filter((user: any) => {
        return (
          user.name.length >= lowerTextBetweenAtSymbolAndAnchor.length &&
          user.name.toLowerCase().startsWith(lowerTextBetweenAtSymbolAndAnchor)
        );
      });

      if (currCandidates.length === 0) {
        toggleMention(false);
      } else {
        toggleMention(true);
        props.setSelectorPos(atSymbolIndex);
      }
      setCandidate(currCandidates);
    }

    return 'not-handled';
  };

  // handle editor changes
  const handleChange = (editorState: EditorState) => {
    const isHandled = mentionChangeHandler(editorState);
    if (isHandled === 'handled') {
      return isHandled;
    }
    if (!editorState.getCurrentContent().hasText()) {
      setEditorStylesActive(false);
    }

    props.setEditorState(editorState);
    handleSelection(editorState);
  };

  function handleKeyCommand(command: any) {
    if (command === 'italic' || command === 'bold' || command === 'underline') {
      if (currentMotionKey !== command) {
        setCurrentMotionKey(command);
      } else {
        setCurrentMotionKey('');
      }
    }
    const newState = RichUtils.handleKeyCommand(props.editorState, command);
    if (newState) {
      props.setEditorState(newState);
      newState.getCurrentInlineStyle().size > 0
        ? setEditorStylesActive(true)
        : setEditorStylesActive(false);

      return 'handled';
    }

    return 'not-handled';
  }

  const handleKeyTab = (e: any) => {
    // On use tab key , prevent hover efect
    if (!preventMouseHover) {
      setPreventMouseHover(true);
    }

    e.preventDefault();
    const hasShiftPressToo = e.shiftKey;
    let userIndex;
    if (hasShiftPressToo) {
      userIndex = userSelectedIndex - 1;
      userIndex = userIndex < 0 ? candidates.length - 1 : userIndex;
    } else {
      userIndex = userSelectedIndex + 1;
      userIndex = userIndex > candidates.length - 1 ? 0 : userIndex;
    }
    setUserSelectedIndex(userIndex);

    if (dropdownRef && dropdownRef.current) {
      const currentSelected =
        dropdownRef.current.ref.current.getElementsByClassName('item')[
          userIndex
        ];
      currentSelected.scrollIntoView({ block: 'start' });
    }
  };
  // ===================================================================
  const keyBindingHandler = (event: any) => {
    if (event.keyCode === 40 && isMention) {
      if (userSelectedIndex + 1 < candidates.length) {
        setUserSelectedIndex(userSelectedIndex + 1);
      } else {
        setUserSelectedIndex(0);
      }
      return 'handled';
    } else if (event.keyCode === 38 && isMention) {
      if (userSelectedIndex === 0) {
        setUserSelectedIndex(candidates.length - 1);
      } else {
        setUserSelectedIndex(userSelectedIndex - 1);
      }
      return 'handled';
    } else if (event.keyCode === 9 && isMention) {
      // Tab
      handleKeyTab(event);
      return 'handled';
    } else if (event.keyCode === 27 && isMention) {
      // ECS to cancel mention list
      toggleMention(!isMention);
      return 'handled';
    } else if (event.keyCode === 13 && isMention) {
      // Enter to select mentioned user in list
      confirmMentionMessage(candidates[userSelectedIndex]);
      setUserSelectedIndex(0);
      return 'handled';
    } else if (event.keyCode === 13 && event.ctrlKey) {
      // Ctrl + Enter to submit message
      if (props.preUpload) {
        props.confirmMutation(props.postMutation, props.preUpload);
      } else {
        props.confirmMutation(props.postMutation);
      }
      return 'handled';
    }

    return getDefaultKeyBinding(event);
  };

  let placeholder = 'Type a message...';
  if (props.activeTag) {
    placeholder = 'Filter mode does not allow messages';
  }
  if (props.disablePlaceholder) {
    placeholder = '';
  }

  return (
    <EditorContainer
      PropsHeight={props.height}
      PropsMinHeight={props.minHeight}
      editorFocused={editorFocused}
    >
      {(editorFeatures || editorStylesActive) && (
        <EditorMotion
          setCurrentMotionKey={setCurrentMotionKey}
          editorState={props.editorState}
          setEditorState={props.setEditorState}
          setEditorFocused={setEditorFocused}
          setEditorStylesActive={setEditorStylesActive}
        />
      )}
      <InputContainer
        PropsHeight={props.height}
        PropsMinHeight={props.minHeight}
      >
        <Editor
          placeholder={placeholder}
          readOnly={!!props.activeTag}
          editorState={props.editorState}
          handleKeyCommand={handleKeyCommand}
          onChange={handleChange}
          stripPastedStyles
          handlePastedText={handlerPasteText}
          keyBindingFn={keyBindingHandler}
          ref={textAreaRef}
        />
        {isMention ? (
          <Mention
            preventMouseHover={preventMouseHover}
            dropdownRef={dropdownRef}
            userList={candidates}
            openDropdown={isMention}
            setUserSelectedIndex={setUserSelectedIndex}
            userSelectedIndex={userSelectedIndex}
            confirmMessage={confirmMentionMessage}
            // if textarea from screenshot page, using caret position.
            // else using fixed position for mention block
            windowPos={props.preUpload ? windowPos : null}
          />
        ) : null}
      </InputContainer>
    </EditorContainer>
  );
}
